import React, { useState } from 'react';
import { Box, Typography, Paper, Collapse } from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import { FaGraduationCap, FaCertificate, FaPlus, FaMinus, FaCheckCircle } from 'react-icons/fa';
import admissionDefault from '../../../../data/admission/admission.json';

const AdmissionEligibility = ({ data }) => {
    const d = data || admissionDefault.eligibility;
    const [activeTab, setActiveTab] = useState('ug');
    const [expandedIndex, setExpandedIndex] = useState(null);

    const handleTabChange = (tab) => {
        setActiveTab(tab);
        setExpandedIndex(null);
    };

    const toggleAccordion = (index) => {
        setExpandedIndex(expandedIndex === index ? null : index);
    };

    const ugData = d.ug || [];
    const pgData = d.pg || [];

    const activeData = activeTab === 'ug' ? ugData : pgData;

    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: { staggerChildren: 0.1 }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 15 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.4 } }
    };

    return (
        <Box sx={{ mb: 6 }}>
            <Box sx={{ mb: 4 }}>
                <Typography component="span" sx={{ fontSize: { xs: '0.75rem', md: '0.875rem' }, fontWeight: 900, letterSpacing: '0.2em', textTransform: 'uppercase', display: 'block', mb: 2, color: '#F6872A' }}>
                    {d.eyebrow}
                </Typography>
                <Typography component="h2" sx={{ fontSize: { xs: '1.75rem', md: '2.25rem', lg: '2.5rem' }, fontWeight: 900, letterSpacing: '-0.025em', mb: 2, lineHeight: 1.2, color: '#0e2455' }}>
                    {d.title}
                </Typography>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 2 }}>
                    <Typography component="p" sx={{ fontSize: { xs: '0.95rem', md: '1rem' }, fontWeight: 500, lineHeight: 1.6, color: '#64748B', maxWidth: '600px' }}>
                        {d.subtitle}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', gap: 2 }}>
                        <Box 
                            onClick={() => handleTabChange('ug')}
                            sx={{ 
                                background: activeTab === 'ug' ? '#0e2455' : '#fff', 
                                border: activeTab === 'ug' ? '1px solid #0e2455' : '1px solid #E2E8F0', 
                                color: activeTab === 'ug' ? '#fff' : '#0e2455', 
                                px: 3, py: 1.2, borderRadius: '50px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.3s'
                            }}
                        >
                            Undergraduate
                        </Box>
                        <Box 
                            onClick={() => handleTabChange('pg')}
                            sx={{ 
                                background: activeTab === 'pg' ? '#0e2455' : '#fff', 
                                border: activeTab === 'pg' ? '1px solid #0e2455' : '1px solid #E2E8F0', 
                                color: activeTab === 'pg' ? '#fff' : '#0e2455', 
                                px: 3, py: 1.2, borderRadius: '50px', fontSize: '0.85rem', fontWeight: 700, cursor: 'pointer', transition: 'all 0.3s' 
                            }}
                        >
                            Postgraduate
                        </Box>
                    </Box>
                </Box>
            </Box>

            <AnimatePresence mode="wait">
                <motion.div
                    key={activeTab}
                    variants={containerVariants}
                    initial="hidden"
                    animate="visible"
                    exit={{ opacity: 0, x: 20 }}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, mb: 3 }}>
                        {activeData.map((item, index) => {
                            const isExpanded = expandedIndex === index;
                            return (
                                <motion.div variants={itemVariants} key={index}>
                                    <Paper
                                        elevation={0}
                                        onClick={() => toggleAccordion(index)}
                                        sx={{
                                            p: 3,
                                            borderRadius: '24px',
                                            border: '1px solid #E2E8F0',
                                            background: '#ffffff',
                                            transition: 'all 0.3s ease',
                                            cursor: 'pointer',
                                            position: 'relative',
                                            overflow: 'hidden',
                                            '&:hover': {
                                                boxShadow: '0 10px 30px rgba(0,0,0,0.05)',
                                                borderColor: '#CBD5E1'
                                            }
                                        }}
                                    >
                                        {isExpanded && (
                                            <Box sx={{ position: 'absolute', top: '50%', left: 0, transform: 'translateY(-50%)', width: '4px', height: '40px', background: '#F6872A', borderRadius: '0 4px 4px 0' }} />
                                        )}
                                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pl: isExpanded ? 2 : 0, transition: 'padding 0.3s' }}>
                                            <Typography sx={{ fontWeight: 800, color: '#0e2455', fontSize: '1.15rem' }}>
                                                {item.title}
                                            </Typography>
                                            <Box sx={{ color: isExpanded ? '#0e2455' : '#38BDF8', transition: 'color 0.3s' }}>
                                                {isExpanded ? <FaMinus size="16px" /> : <FaPlus size="16px" />}
                                            </Box>
                                        </Box>
                                        
                                        <AnimatePresence>
                                            {isExpanded && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    transition={{ duration: 0.3 }}
                                                >
                                                    <Box sx={{ mt: 3, pt: 3, borderTop: '1px solid #E2E8F0', pl: 2 }}>
                                                        {item.points.map((point, pIndex) => (
                                                            <Box key={pIndex} sx={{ display: 'flex', gap: 2, mb: 2, alignItems: 'flex-start' }}>
                                                                <Box sx={{ color: '#F6872A', mt: 0.5 }}>
                                                                    <FaCheckCircle size="16px" />
                                                                </Box>
                                                                <Typography sx={{ color: '#64748B', fontSize: '0.95rem', lineHeight: 1.6 }}>
                                                                    {point}
                                                                </Typography>
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                </motion.div>
                                            )}
                                        </AnimatePresence>
                                    </Paper>
                                </motion.div>
                            );
                        })}
                    </Box>
                </motion.div>
            </AnimatePresence>
        </Box>
    );
};

export default AdmissionEligibility;
